import { useState, useEffect } from "react";
import { FolderOpen, Folder, CheckCircle2, AlertCircle, Download, ChevronRight, ExternalLink } from "lucide-react";
import { pickLedgerDir, hasLedgerDir } from "../services/inventory";

interface Props {
  onComplete: () => void;
}

export default function Setup({ onComplete }: Props) {
  const [state, setState] = useState<"idle" | "picking" | "done" | "error">("idle");
  const [path, setPath] = useState("");
  const [installPrompt, setInstallPrompt] = useState<Event | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handlePick = async () => {
    setState("picking");
    try {
      const handle = await pickLedgerDir();
      setPath(handle.name);
      setState("done");
      setTimeout(() => {
        hasLedgerDir().then((ok) => {
          if (ok) onComplete();
        });
      }, 500);
    } catch (e: any) {
      if (e?.name !== "AbortError" && e?.name !== "SecurityError") {
        setState("error");
      } else {
        setState("idle");
      }
    }
  };

  const handleInstall = () => {
    if (installPrompt) {
      (installPrompt as any).prompt();
    }
  };

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center p-6">
      <div className="max-w-lg w-full space-y-8 text-center">
        {/* Logo */}
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-white">
            Mavelux <span className="text-citilux-blue-400">paints</span>
          </h1>
          <p className="text-metallic-silver/50 text-sm mt-1 font-medium tracking-widest uppercase">
            Inventory Manager
          </p>
        </div>

        {/* PWA Install prompt */}
        {installPrompt && (
          <button
            onClick={handleInstall}
            className="w-full flex items-center justify-between gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-5 py-4 text-left transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-citilux-blue-600/20 flex items-center justify-center">
                <Download className="w-5 h-5 text-citilux-blue-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white group-hover:text-citilux-blue-400 transition-colors">
                  Install as Desktop App
                </p>
                <p className="text-xs text-metallic-silver/50">
                  Opens in its own window, works offline
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-metallic-silver/30 group-hover:text-citilux-blue-400 transition-colors" />
          </button>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3 text-metallic-silver/30 text-xs uppercase tracking-widest">
          <div className="flex-1 h-px bg-white/10" />
          <span>Get Started</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Folder picker */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3 justify-center">
            <Folder className="w-5 h-5 text-citilux-blue-400" />
            <p className="text-sm text-metallic-silver/70">
              Point to your <code className="text-citilux-blue-400 bg-navy-800 px-1.5 py-0.5 rounded text-xs">Mavelux_Ledger</code> folder
            </p>
          </div>

          {state === "idle" && (
            <button
              onClick={handlePick}
              className="w-full flex items-center justify-center gap-3 bg-citilux-blue-600 hover:bg-citilux-blue-500 text-white rounded-xl px-6 py-3 font-semibold transition-colors"
            >
              <FolderOpen className="w-5 h-5" />
              Select Folder
            </button>
          )}

          {state === "picking" && (
            <div className="flex items-center justify-center gap-2 text-metallic-silver/60 text-sm py-3">
              <div className="w-5 h-5 border-2 border-citilux-blue-500 border-t-transparent rounded-full animate-spin" />
              Opening folder picker...
            </div>
          )}

          {state === "done" && (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-2 text-green-400 justify-center">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Connected</span>
              </div>
              <p className="text-xs text-metallic-silver/50">Redirecting to Dashboard...</p>
            </div>
          )}

          {state === "error" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-red-400 justify-center">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">Could not access the folder</span>
              </div>
              <button
                onClick={handlePick}
                className="text-sm text-citilux-blue-400 hover:underline"
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="text-xs text-metallic-silver/40 space-y-1">
          <p>Works in Chrome or Edge on Windows.</p>
          <p>Your data stays on your machine — no cloud sync.</p>
        </div>
      </div>
    </div>
  );
}
